/**
 * About AskIsaac - Company Identity & Mission Page
 * "The Infrastructure for Healthcare Resilience"
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import {
  Shield,
  Zap,
  Network,
  Clock,
  CheckCircle2,
  Building2,
  Wrench,
  Users,
  ArrowRight,
  ArrowLeft,
  Play,
  Award,
  Target,
  TrendingUp,
  Globe,
  HeartPulse,
  Cpu,
  Layers,
  Home,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Healthcare environment images (placeholder URLs - would be replaced with actual images)
const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=800",
    alt: "Operating Room",
    caption: "Surgical Suites"
  },
  {
    url: "https://images.unsplash.com/photo-1516549655169-df83a0774514?w=800",
    alt: "Medical Equipment",
    caption: "Medical Equipment"
  },
  {
    url: "https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=800",
    alt: "Hospital Facility",
    caption: "Facility Engineering"
  }
];

// Sterile Mate company-level pillars (founder-voice — distinct from AskIsaac platform features)
const STERILE_MATE_PILLARS = [
  {
    icon: HeartPulse,
    title: "Service to Healthcare",
    tagline: "Solve the problems that keep clinicians focused on the patient in front of them.",
    description: "We exist because hospitals deserve a partner who shows up when the primary supplier says \u201cout of stock,\u201d when the water loop fails, when the SPD project needs to be done right the first time. Every order, every install, every backorder triage is engineered around one thing\u2014keeping clinical teams unencumbered.",
    accent: "bg-blue-600",
    ring: "ring-blue-100",
    testid: "sm-pillar-service"
  },
  {
    icon: Users,
    title: "Jobs for Our Community",
    tagline: "Translate every dollar of revenue into real jobs, real paychecks, real career pathways.",
    description: "Sterile Mate is a Houston business and a Houston employer. Workforce inclusion\u2014particularly for people with disabilities and historically underemployed populations\u2014is not a CSR line item. It is how we hire, how we promote, and how we grow.",
    accent: "bg-amber-500",
    ring: "ring-amber-100",
    testid: "sm-pillar-community"
  },
  {
    icon: TrendingUp,
    title: "Sustainable Growth, Earned",
    tagline: "Grow only on the strength of measurable value delivered to our clients.",
    description: "Our growth model compounds on client value\u2014never extractively, never faster than the trust we have built. If we cannot prove the value, we do not earn the next order. That discipline keeps us honest, keeps our clients at the center of every decision, and keeps the business durable through any market cycle.",
    accent: "bg-emerald-600",
    ring: "ring-emerald-100",
    testid: "sm-pillar-growth"
  }
];

// Core pillars of AskIsaac
const PILLARS = [
  {
    icon: Zap,
    title: "High-Velocity Command Engine",
    description: "Replace 'sticky-note' logistics with real-time supply chain orchestration. From backorder to delivery in hours, not weeks.",
    color: "bg-amber-500"
  },
  {
    icon: Network,
    title: "National Contractor Network",
    description: "Access thousands of vetted contractors through our Vendor Fortress. Every provider is compliance-verified and performance-tracked.",
    color: "bg-blue-500"
  },
  {
    icon: Shield,
    title: "100% Operational Uptime",
    description: "Crisis-tested technology designed for the worst-case scenario. When Baxter fluids run out, AskIsaac keeps your OR running.",
    color: "bg-emerald-500"
  }
];

// Stats - Honest, capability-focused metrics
const STATS = [
  { value: "24/7", label: "Platform Availability", icon: Clock },
  { value: "3-Quote", label: "Texas Compliant", icon: Shield },
  { value: "<48hr", label: "Avg. Response Time", icon: Zap },
  { value: "100%", label: "Audit Trail Coverage", icon: CheckCircle2 }
];

// Mission points
const MISSION_POINTS = [
  {
    title: "Bridge the Gap",
    description: "Connect hospital facilities with a national network of vetted contractors, suppliers, and service providers.",
    icon: Network
  },
  {
    title: "Eliminate Chaos",
    description: "Transform reactive 'firefighting' into proactive supply chain management with predictive analytics.",
    icon: Target
  },
  {
    title: "Ensure Resilience",
    description: "Build crisis-tested infrastructure that keeps hospitals operational when traditional supply chains fail.",
    icon: Shield
  }
];

export default function AboutPage() {
  const [activeImage, setActiveImage] = useState(0);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        canonical="/about"
        title="About Sterile Mate — Hospital Supply Chain Infrastructure"
        description="Sterile Mate has served hospitals across the Gulf region for 6+ years — Texas Medical Center, Louisiana, Alabama, Mississippi. Meet the team building the infrastructure for healthcare resilience, powered by AskIsaac."
        keywords={["Sterile Mate LLC", "Sterile Mate Houston", "healthcare infrastructure company Texas", "AskIsaac"]}
      />
      {/* Site-wide public header (dark navy, dual logo, full nav incl. About) */}
      <PublicSiteHeader />

      {/* Hero Section — dark navy, aligned with homepage hero aesthetic */}
      <section
        className="relative overflow-hidden bg-[#0a0f1a]"
        data-testid="about-hero"
      >
        {/* Subtle radial glow + grid texture */}
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 30% 0%, rgba(0,86,168,0.35) 0%, rgba(10,15,26,0) 60%), radial-gradient(50% 40% at 90% 100%, rgba(8,145,178,0.18) 0%, rgba(10,15,26,0) 60%)"
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "44px 44px"
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-20 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — Honest positioning copy */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900/80 border border-cyan-400/30 text-cyan-300 rounded-full text-[11px] font-semibold tracking-widest uppercase mb-6 backdrop-blur-sm"
                data-testid="about-hero-badge"
              >
                <HeartPulse className="w-3.5 h-3.5" />
                Sterile Mate &middot; AskIsaac &middot; Houston, TX
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6 tracking-tight">
                The operating backbone of
                <span className="block text-cyan-300">modern healthcare procurement.</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-5">
                <strong className="text-white">Sterile Mate</strong> is a Houston-based medical device distributor and healthcare infrastructure partner. We deliver the surgical instruments, water systems, skilled trades, and capital projects that hospitals and surgical facilities across Texas depend on every day.
              </p>
              <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-8">
                <strong className="text-white">AskIsaac</strong> is the intelligence platform we built to run that work &mdash; routing backorder hunts, dispatching maintenance, generating compliant 3-quote packages, and orchestrating contractors in real time so our clients never wait on procurement again.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link to="/marketplace">
                  <Button
                    size="lg"
                    className="bg-[#0056A8] hover:bg-[#003366] text-white rounded-lg px-6 h-12 text-sm font-semibold"
                    data-testid="about-hero-cta-marketplace"
                  >
                    Browse the Marketplace
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-600 bg-slate-900/40 text-white hover:bg-slate-800 hover:text-white rounded-lg px-6 h-12 text-sm font-semibold"
                    data-testid="about-hero-cta-howitworks"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    See How It Works
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right — Image carousel */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] ring-1 ring-white/10">
                <img
                  src={HERO_IMAGES[activeImage].url}
                  alt={HERO_IMAGES[activeImage].alt}
                  className="w-full h-[420px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-6">
                  <p className="text-white font-semibold text-sm tracking-wide">
                    {HERO_IMAGES[activeImage].caption}
                  </p>
                </div>
              </div>

              {/* Thumb selector */}
              <div className="flex justify-center gap-3 mt-4">
                {HERO_IMAGES.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                      activeImage === idx
                        ? "border-cyan-400 scale-105"
                        : "border-transparent opacity-50 hover:opacity-100"
                    }`}
                    aria-label={`Select ${img.alt}`}
                    data-testid={`about-hero-thumb-${idx}`}
                  >
                    <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <p className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Healthcare Leaders Choose AskIsaac
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Built for the entire hospital ecosystem—from Operating Rooms to Facility Engineering
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {PILLARS.map((pillar, idx) => (
              <div 
                key={idx}
                className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-shadow"
              >
                <div className={`w-14 h-14 ${pillar.color} rounded-xl flex items-center justify-center mb-6`}>
                  <pillar.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{pillar.title}</h3>
                <p className="text-slate-600 leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sterile Mate — Our Three Pillars (company-level, founder-voice) */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="sterile-mate-pillars-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-full text-[11px] font-semibold tracking-widest uppercase mb-5">
              <Award className="w-3.5 h-3.5" />
              Sterile Mate &middot; The Company Behind AskIsaac
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
              Our Three Pillars
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              AskIsaac is the platform. Sterile Mate is the company behind it &mdash; a Houston-based medical-device distributor and healthcare infrastructure operator. These are the three commitments that govern how we run the business, who we hire, and how we grow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {STERILE_MATE_PILLARS.map((pillar, idx) => (
              <div
                key={idx}
                data-testid={pillar.testid}
                className={`group relative bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ring-1 ring-transparent hover:${pillar.ring}`}
              >
                {/* Pillar number watermark */}
                <span className="absolute top-5 right-6 text-[64px] font-black text-slate-100 leading-none select-none group-hover:text-slate-200 transition-colors" aria-hidden="true">
                  {String(idx + 1).padStart(2, "0")}
                </span>

                <div className={`relative w-14 h-14 ${pillar.accent} rounded-xl flex items-center justify-center mb-6 shadow-md`}>
                  <pillar.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="relative text-xl font-bold text-slate-900 mb-3">{pillar.title}</h3>
                <p className="relative text-slate-900 font-semibold text-[15px] leading-snug mb-4">
                  {pillar.tagline}
                </p>
                <p className="relative text-slate-600 text-sm leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>

          {/* Closing tie-back */}
          <div className="mt-12 max-w-4xl">
            <blockquote className="border-l-4 border-slate-900 pl-5 py-1 text-slate-700 italic text-base md:text-lg leading-relaxed">
              Three pillars, one operating philosophy: serve the healthcare professionals who serve our communities, employ the neighbors who make that service possible, and grow only as fast as the value we deliver lets us. Everything Sterile Mate ships, installs, hires, and builds is measured against these three.
            </blockquote>
          </div>
        </div>
      </section>

      {/* ===================================================================
           Founder & CEO Bio — George Broughton III
           Anchored at /about#founder for direct linking from LinkedIn,
           email signatures, and Google Knowledge Panel bio requests.
      =================================================================== */}
      <section
        id="founder"
        className="py-20 bg-white border-y border-slate-200 scroll-mt-24"
        data-testid="about-founder-bio-section"
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-full text-[11px] font-semibold tracking-widest uppercase mb-6">
            <Users className="w-3.5 h-3.5" />
            Leadership &middot; Founder &amp; CEO
          </div>

          <div className="grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-14 items-start">
            {/* Portrait */}
            <div className="mx-auto lg:mx-0">
              <div className="relative w-64 h-64 lg:w-[280px] lg:h-[280px] rounded-2xl overflow-hidden ring-2 ring-cyan-500/30 shadow-2xl">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/george_broughton_portrait.jpg`}
                  alt="George Broughton III, Founder &amp; CEO of Sterile Mate LLC"
                  className="w-full h-full object-cover"
                  data-testid="about-founder-portrait"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/70 to-transparent p-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-200">
                    Houston, TX
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-cyan-700" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z"/></svg>
                  <a href="https://www.linkedin.com/in/georgebroughtoniii" target="_blank" rel="noreferrer" className="hover:text-cyan-700 font-semibold">LinkedIn</a>
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-4 h-4 text-cyan-700" aria-hidden>&#9993;</span>
                  <a href="mailto:george.broughton@sterilemate.com" className="hover:text-cyan-700 font-semibold">george.broughton@sterilemate.com</a>
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-4 h-4 text-cyan-700" aria-hidden>&#128222;</span>
                  <a href="tel:2814563458" className="hover:text-cyan-700 font-semibold">281.456.3458</a>
                </p>
              </div>
            </div>

            {/* Bio content */}
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-2">
                George Broughton III
              </h2>
              <p className="text-base md:text-lg text-cyan-700 font-bold mb-5">
                Founder &amp; Chief Executive Officer &middot; Sterile Mate LLC
              </p>

              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  George Broughton III is the founder and Chief Executive Officer of <strong>Sterile Mate LLC</strong>, a Houston-based hospital supply-chain and healthcare-infrastructure company. Since 2021 he has built Sterile Mate into a trusted vendor for Gulf-region hospital systems &mdash; sourcing hard-to-find medical devices, delivering AAMI-compliant critical-water systems for sterile processing, and leading capital projects inside occupied clinical environments.
                </p>
                <p>
                  George is the architect of <strong>AskIsaac</strong>, the intelligence layer behind sterilemate.com that turns hospital backorder shortages into live RFQs pushed directly to a vetted contractor and supplier network. Under his leadership, Sterile Mate holds active reverse-osmosis / DI polisher contracts across multiple Houston-area hospital campuses, is an Authorized Distributor for American Surgical (Neuro Sponges &amp; Cottonoids), and serves as a subject-matter expert on the <strong>AAMI ST108</strong> critical-water standard for sterile processing departments.
                </p>
                <p>
                  Before founding Sterile Mate, George spent 14 years at <strong>United Airlines</strong> designing and delivering FAA-compliant curriculum for new-hire and recurrent flight-attendant training &mdash; the discipline of running mission-critical operations under regulatory scrutiny is what shapes Sterile Mate&apos;s operating standards today. His earlier career includes national sales &amp; marketing leadership for a Chicago startup apparel brand and account-executive roles across the wireless industry.
                </p>
                <p>
                  Sterile Mate is <strong>SAM.gov registered</strong> (CAGE 9GH66), a certified <strong>Minority Business Enterprise</strong>, and operates from a distribution facility in the North Houston District &mdash; where George was featured on the cover of the District&apos;s Quadrants publication (Oct 2023) as a profiled MBE business owner.
                </p>
              </div>

              {/* Quick facts grid */}
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { k: "Founded", v: "2021" },
                  { k: "HQ", v: "Houston, TX" },
                  { k: "SAM.gov CAGE", v: "9GH66" },
                  { k: "Certification", v: "MBE" },
                ].map((f) => (
                  <div key={f.k} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{f.k}</p>
                    <p className="text-sm font-bold text-slate-900 mt-0.5">{f.v}</p>
                  </div>
                ))}
              </div>

              {/* CTA row */}
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="https://www.linkedin.com/in/georgebroughtoniii"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-5 h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold shadow-md"
                  data-testid="about-founder-linkedin-cta"
                >
                  Connect on LinkedIn <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Mission Statement */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-full text-[11px] font-semibold tracking-widest uppercase mb-5">
                <Target className="w-3.5 h-3.5" />
                Our Mission
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Ensuring Every Hospital Can Weather Any Storm
              </h2>
              
              <p className="text-lg text-slate-600 mb-8">
                When supply chains collapse and equipment fails, hospitals can&apos;t afford downtime. 
                AskIsaac was built during the 2024 Baxter fluid shortage—a crisis that exposed 
                the fragility of healthcare logistics. We exist to ensure that never happens again.
              </p>

              <div className="space-y-6">
                {MISSION_POINTS.map((point, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <point.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{point.title}</h4>
                      <p className="text-slate-600">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Vision Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Globe className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Our Vision</h3>
                  <p className="text-sm text-slate-500">The future of healthcare operations</p>
                </div>
              </div>
              
              <blockquote className="text-xl text-slate-700 italic mb-6 border-l-4 border-blue-600 pl-4">
                &ldquo;A world where no patient&apos;s care is ever compromised by supply chain failures, 
                equipment downtime, or contractor unavailability.&rdquo;
              </blockquote>
              
              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-slate-200">
                <div className="text-center">
                  <Cpu className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-900">AI-Powered</p>
                  <p className="text-xs text-slate-500">Predictive analytics</p>
                </div>
                <div className="text-center">
                  <Layers className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-900">Integrated</p>
                  <p className="text-xs text-slate-500">One platform for all</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Hospital Operations?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            See how AskIsaac can streamline your maintenance, backorders, and supplier network.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/marketplace">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="about-footer-cta-marketplace">
                Browse the Marketplace
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="mailto:info@sterilemate.com">
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                Contact Sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
